import React, { useEffect, useLayoutEffect, useRef, useState } from "react"

const FocusGrow = ({
  children,
  className,
  growClass,
  radiusMultiplier = 1,
  radiusOffset = { x: 0, y: 0 },
  isExpandedExternal, // New prop for external control
  onExpandedChange, // Optional callback for expansion state changes
}) => {
  const container = useRef(null)
  const [isExpandedInternal, setIsExpandedInternal] = useState(false)
  const [expandedSize, setExpandedSize] = useState(0)
  const [initialSize, setInitialSize] = useState(0)
  const [circlePos, setCirclePos] = useState({ x: 0, y: 0 })

  // Use external control if provided, otherwise use internal state
  const isExpanded =
    isExpandedExternal !== undefined ? isExpandedExternal : isExpandedInternal

  useEffect(() => {
    const currentContainer = container.current
    const parent = currentContainer.parentElement
    let hasValue = false

    const updateCirclePosition = () => {
      if (!currentContainer?.children[0]) return

      const firstChildRect =
        currentContainer.children[0].getBoundingClientRect()
      const containerRect = currentContainer.getBoundingClientRect()

      setInitialSize(radiusMultiplier * firstChildRect.width)

      const x =
        firstChildRect.left -
        containerRect.left +
        firstChildRect.width / 2 +
        radiusOffset.x
      const y =
        firstChildRect.top -
        containerRect.top +
        firstChildRect.height / 2 +
        radiusOffset.y

      hasValue = y !== 0
      setCirclePos({ x, y })

      const parentRect = parent.getBoundingClientRect()
      setExpandedSize(parentRect.width * 2.4)
    }

    updateCirclePosition()

    let updateTimer = setInterval(() => {
      updateCirclePosition()
      if (!hasValue) return
      clearInterval(updateTimer)
    }, 16)

    const handleResize = () => {
      window.requestAnimationFrame(updateCirclePosition)
    }

    const handleMouseEnter = () => {
      if (isExpandedExternal === undefined) {
        setIsExpandedInternal(true)
        onExpandedChange?.(true)
      }
    }

    const handleMouseLeave = () => {
      if (isExpandedExternal === undefined) {
        setIsExpandedInternal(false)
        onExpandedChange?.(false)
      }
    }

    window.addEventListener("resize", handleResize)

    // Only add mouse listeners if not externally controlled
    if (isExpandedExternal === undefined) {
      parent.addEventListener("mouseenter", handleMouseEnter)
      parent.addEventListener("mouseleave", handleMouseLeave)
    }

    return () => {
      window.removeEventListener("resize", handleResize)
      if (isExpandedExternal === undefined) {
        parent.removeEventListener("mouseenter", handleMouseEnter)
        parent.removeEventListener("mouseleave", handleMouseLeave)
      }
      clearInterval(updateTimer)
    }
  }, [
    isExpandedExternal,
    onExpandedChange,
    radiusMultiplier,
    radiusOffset.x,
    radiusOffset.y,
  ])

  return (
    <div ref={container} className={`${className}`}>
      {children}
      <div
        className={`${growClass} block absolute rounded-full transition-all duration-500 ease-in-out`}
        style={{
          width: isExpanded ? `${expandedSize}px` : `${initialSize}px`,
          height: isExpanded ? `${expandedSize}px` : `${initialSize}px`,
          top: `${circlePos.y}px`,
          left: `${circlePos.x}px`,
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  )
}

export default FocusGrow
